import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Truck, UserCircle, Eye, EyeOff, Crown } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useGoogleLogin } from '@react-oauth/google';

const Auth = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [role, setRole] = useState('customer');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'driver') {
        navigate('/driver/dashboard', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate('/customer/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if terms are accepted
    if (!acceptedTerms) {
      toast({
        title: 'Terms Required',
        description: 'Please accept the Terms of Service to continue',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      let response;
      if (isLogin) {
        response = await authAPI.login(formData.email, formData.password, role);
      } else {
        response = await authAPI.register(formData.email, formData.password, formData.name, role);
        
        // Apply referral code if provided (only for customer signups)
        if (role === 'customer' && referralCode.trim()) {
          try {
            const { referralAPI } = await import('../services/api');
            await referralAPI.applyCode(referralCode.trim());
            toast({
              title: 'Referral Applied!',
              description: 'You and your referrer will earn £5 when you make your first £50+ purchase',
              duration: 5000
            });
          } catch (refError) {
            // Don't block signup if referral fails, just notify
            console.error('Referral application failed:', refError);
          }
        }
      }
      
      login(response.user, response.token);
      toast({
        title: isLogin ? 'Login Successful' : 'Account Created',
        description: `Welcome ${response.user.name}!`
      });
      
      // Navigate based on actual role returned from server
      const actualRole = response.user.role;
      if (actualRole === 'driver') {
        navigate('/driver/dashboard');
      } else if (actualRole === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/customer/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Authentication failed',
        variant: 'destructive'
      });
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Check if terms are accepted
        if (!acceptedTerms) {
          toast({
            title: 'Terms Required',
            description: 'Please accept the Terms of Service to continue with Google Sign-In',
            variant: 'destructive'
          });
          return;
        }

        // Send the access token to backend for verification
        // Always send 'customer' as default for login flow — backend uses DB role for existing users
        const googleRole = isLogin ? 'customer' : role;
        const response = await authAPI.googleLogin(tokenResponse.access_token, googleRole);
        
        // Check if this was a new account creation
        const isNewAccount = response.user.created_at && 
          new Date(response.user.created_at).getTime() > Date.now() - 5000; // Created in last 5 seconds
        
        login(response.user, response.token);
        
        toast({
          title: isNewAccount ? 'Account Created!' : 'Login Successful',
          description: isNewAccount 
            ? `Welcome to Wheely Rescue, ${response.user.name}! Your account has been created.`
            : `Welcome back, ${response.user.name}!`
        });
        
        // Navigate based on actual role returned from server
        const actualRole = response.user.role;
        if (actualRole === 'driver') {
          navigate('/driver/dashboard');
        } else if (actualRole === 'admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/customer/dashboard');
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Google sign-in failed',
          variant: 'destructive'
        });
      }
    },
    onError: () => {
      toast({
        title: 'Google Sign-In Cancelled',
        description: 'Please try again or use email/password',
        variant: 'destructive'
      });
    }
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0F1D2B' }}>
      <Card className="w-full max-w-md" style={{ background: 'rgba(23, 40, 58, 0.98)', borderColor: 'rgba(225, 116, 49, 0.25)' }}>
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Wheely Rescue Logo" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <CardDescription className="text-wheely-orange-light text-sm">
            Emergency roadside assistance at your fingertips
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={isLogin ? 'login' : 'signup'} onValueChange={(v) => setIsLogin(v === 'login')} className="w-full">
            <TabsList className="grid w-full grid-cols-2" style={{ background: 'rgba(15, 29, 43, 0.9)' }}>
              <TabsTrigger value="login" className="data-[state=active]:text-white" style={{ background: isLogin ? 'linear-gradient(to right, #E17431, #EB9868)' : 'transparent' }}>Login</TabsTrigger>
              <TabsTrigger value="signup" className="data-[state=active]:text-white" style={{ background: !isLogin ? 'linear-gradient(to right, #E17431, #EB9868)' : 'transparent' }}>Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="space-y-4 mt-4">
              <div className="space-y-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                      className="text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-white">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                        className="text-white placeholder:text-gray-400 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-wheely-orange-light transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Terms of Service Checkbox */}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms-login"
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                      className="mt-1"
                    />
                    <Label htmlFor="terms-login" className="text-sm text-gray-300 cursor-pointer">
                      I agree to the{' '}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wheely-orange-light hover:text-wheely-orange-light underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wheely-orange-light hover:text-wheely-orange-light underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                    </Label>
                  </div>
                  
                  <Button type="submit" className="w-full text-white font-semibold" style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}>
                    Login
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" style={{ borderColor: 'rgba(225, 116, 49, 0.3)' }} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-2 text-gray-400" style={{ background: '#0F1D2B' }}>Or continue with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full bg-white hover:bg-gray-100 text-black font-semibold"
                  onClick={handleGoogleLogin}
                  disabled={!acceptedTerms}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="role-signup" className="text-white">I am a</Label>
                  <RadioGroup value={role} onValueChange={setRole} className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="customer" id="customer-signup" style={{ borderColor: '#E17431', color: '#E17431' }} />
                      <Label htmlFor="customer-signup" className="text-gray-200 cursor-pointer">Customer</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="driver" id="driver-signup" style={{ borderColor: '#E17431', color: '#E17431' }} />
                      <Label htmlFor="driver-signup" className="text-gray-200 cursor-pointer">Driver</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                {/* Membership Info for Drivers */}
                {role === 'driver' && (
                  <div className="p-4 rounded-lg border" style={{ 
                    background: 'linear-gradient(to bottom right, rgba(235, 152, 104, 0.1), rgba(225, 116, 49, 0.08))',
                    borderColor: 'rgba(235, 152, 104, 0.3)'
                  }}>
                    <div className="flex items-start gap-3">
                      <Crown className="w-5 h-5 text-wheely-orange-light mt-1 flex-shrink-0" />
                      <div className="space-y-2">
                        <h3 className="text-white font-semibold text-sm">Membership Required</h3>
                        <p className="text-gray-300 text-xs leading-relaxed">
                          To start receiving service requests and earn money, you&apos;ll need an active membership subscription. Choose from Bronze, Silver, or Gold tiers with commission rates from 2.5% to 5%.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <div className="px-3 py-1.5 rounded-md bg-orange-900/30 border border-wheely-orange/30">
                            <span className="text-xs text-orange-300 font-medium">Bronze: 5%</span>
                          </div>
                          <div className="px-3 py-1.5 rounded-md bg-gray-700/30 border border-wheely-navy-700/30">
                            <span className="text-xs text-gray-300 font-medium">Silver: 3.5%</span>
                          </div>
                          <div className="px-3 py-1.5 rounded-md bg-yellow-900/30 border border-yellow-500/30">
                            <span className="text-xs text-yellow-300 font-medium">Gold: 2.5%</span>
                          </div>
                        </div>
                        <p className="text-wheely-orange-light text-xs mt-2 font-semibold">
                          ✓ Free 3-month Bronze trial available after signup!
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                      className="text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-signup" className="text-white">Email</Label>
                    <Input
                      id="email-signup"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                      className="text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password-signup" className="text-white">Password</Label>
                    <div className="relative">
                      <Input
                        id="password-signup"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                        className="text-white placeholder:text-gray-400 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-wheely-orange-light transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Referral Code Input (only for customers) */}
                  {role === 'customer' && (
                    <div className="space-y-2">
                      <Label htmlFor="referral-code" className="text-white">
                        Referral Code <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Input
                        id="referral-code"
                        name="referral-code"
                        type="text"
                        placeholder="Enter code from a friend"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        style={{ background: 'rgba(15, 29, 43, 0.9)', borderColor: 'rgba(225, 116, 49, 0.3)' }}
                        className="text-white placeholder:text-gray-400"
                      />
                      <p className="text-xs text-wheely-orange-light">
                        You and your referrer will each get £5 credit when you spend £50+ within 12 months
                      </p>
                    </div>
                  )}
                  
                  {/* Terms of Service Checkbox */}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms-signup"
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                      className="mt-1"
                    />
                    <Label htmlFor="terms-signup" className="text-sm text-gray-300 cursor-pointer">
                      I agree to the{' '}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wheely-orange-light hover:text-wheely-orange-light underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wheely-orange-light hover:text-wheely-orange-light underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                    </Label>
                  </div>
                  
                  <Button type="submit" className="w-full text-white font-semibold" style={{ background: 'linear-gradient(to right, #E17431, #EB9868)' }}>
                    Create Account
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" style={{ borderColor: 'rgba(225, 116, 49, 0.3)' }} />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-2 text-gray-400" style={{ background: '#0F1D2B' }}>Or continue with</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full bg-white hover:bg-gray-100 text-black font-semibold"
                  onClick={handleGoogleLogin}
                  disabled={!acceptedTerms}
                >
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;